import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, AlertTriangle } from "lucide-react";
import { usePermission } from "@/lib/usePermission";

/**
 * Phase TOLLBILL-P3a (2026-07-10) — Global toll-markup settings page.
 *
 * Wires GET/PATCH /admin/tolls/settings. The single ``tolls_markup_percent``
 * field is validated client-side (0-100, step 0.5) and server-side
 * (422 with a friendly message if out of range).
 *
 * Per-customer overrides live on the customer detail edit page — this
 * page is scoped strictly to the tenant-wide default.
 */
export default function AdminTollsSettingsPage() {
    const [pct, setPct] = useState("");
    const [serverPct, setServerPct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const { ready: permReady, hasPermission } = usePermission();
    const canView = hasPermission("Tolls", "View");
    const canManage = hasPermission("Tolls", "Manage");

    useEffect(() => {
        if (!permReady) return;
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        api.get("/admin/tolls/settings")
            .then(({ data }) => {
                const p = Number(data.tolls_markup_percent || 0);
                setPct(String(p));
                setServerPct(p);
            })
            .catch((err) => setError(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [permReady, canView]);

    const save = async () => {
        setError(null);
        const parsed = Number(pct);
        if (Number.isNaN(parsed)) { setError("Enter a numeric percent (0-100)."); return; }
        if (parsed < 0 || parsed > 100) { setError("Value must be between 0 and 100."); return; }
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/tolls/settings", { tolls_markup_percent: parsed });
            setServerPct(Number(data.tolls_markup_percent || 0));
            setPct(String(data.tolls_markup_percent));
            toast.success("Toll markup updated");
        } catch (err) {
            const msg = formatApiError(err);
            setError(msg);
            toast.error(msg);
        } finally { setSaving(false); }
    };

    if (permReady && !canView) {
        return (
            <DashboardShell>
                <Card className="mt-8">
                    <CardContent className="p-10 text-center">
                        <AlertTriangle size={28} className="mx-auto text-amber-500" />
                        <h2 className="mt-3 font-display text-xl font-semibold" data-testid="toll-settings-access-denied">Access denied</h2>
                        <p className="mt-1 text-sm text-slate-600">You do not have permission to view toll settings.</p>
                    </CardContent>
                </Card>
            </DashboardShell>
        );
    }

    const dirty = permReady && canView && String(serverPct) !== pct;

    return (
        <DashboardShell>
            <div className="fade-up">
                <Button asChild variant="ghost" className="gap-1.5 mb-2 -ml-2" data-testid="toll-settings-back">
                    <Link to="/admin/tolls"><ArrowLeft size={14} /> Back to Tolls</Link>
                </Button>
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Ancillary billing</p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Toll settings</h1>
                <p className="text-sm text-slate-500 mt-1">Global markup applied to matched toll transactions when draft invoices are generated.</p>
            </div>

            <Card className="mt-6 max-w-2xl">
                <CardContent className="p-6 space-y-4">
                    {loading && <p className="text-sm text-slate-500" data-testid="toll-settings-loading">Loading…</p>}
                    {!loading && (
                        <>
                            <div>
                                <Label className="text-sm font-medium">Global markup percent</Label>
                                <div className="flex items-center gap-2 mt-1">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        value={pct}
                                        onChange={(e) => setPct(e.target.value)}
                                        disabled={!canManage || saving}
                                        className="w-40 tabular-nums"
                                        data-testid="toll-settings-input"
                                    />
                                    <span className="text-slate-500 text-sm">%</span>
                                </div>
                                <p className="text-xs text-slate-500 mt-2">
                                    Applied as a percentage markup on toll amounts when generating draft toll invoices.
                                    Per-customer overrides take precedence when set.
                                </p>
                                {error && (
                                    <p className="text-xs text-red-700 mt-2" data-testid="toll-settings-error">{error}</p>
                                )}
                            </div>

                            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                                <p>
                                    Per-customer overrides are edited on each customer's{" "}
                                    <Link to="/admin/customers" className="text-blue-900 hover:underline">customer detail page</Link>.
                                    Coming in Phase 3b — until then, use{" "}
                                    <code className="font-mono text-[11px] bg-white px-1 rounded">PATCH /api/admin/customers/{"{id}"}/toll-markup</code>{" "}
                                    directly.
                                </p>
                            </div>

                            {canManage && (
                                <div className="flex justify-end gap-2 pt-2">
                                    <Button
                                        onClick={save}
                                        disabled={saving || !dirty}
                                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                        data-testid="toll-settings-save"
                                    >
                                        <Save size={14} /> {saving ? "Saving…" : "Save"}
                                    </Button>
                                </div>
                            )}
                            {!canManage && (
                                <p className="text-xs text-slate-500 italic" data-testid="toll-settings-readonly">
                                    Read-only — you need the "Tolls / Manage" permission to change this setting.
                                </p>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </DashboardShell>
    );
}
